/**
 * Holds the first pageview until auth has identified or reset.
 *
 * AnalyticsProvider is a child of AuthProvider, so its passive effects run first.
 * A pageview in that window lands on a distinct id that `analytics.reset()` then
 * abandons, which is how anonymous persons pile up on `/login`. Auth holds the
 * gate during render-adjacent layout work and releases it after identity settles.
 * The default is open so tests that mount AnalyticsProvider alone still pageview.
 */

let held = false;
const listeners = new Set<() => void>();

export function holdPageviews(): void {
    held = true;
}

export function pageviewsAreHeld(): boolean {
    return held;
}

/** Notifies subscribers only on the held → open transition, so a repeat release cannot loop. */
export function releasePageviews(): void {
    if (!held) {
        return;
    }

    held = false;
    for (const listener of listeners) {
        listener();
    }
}

export function subscribePageviewGate(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Clears module state between Vitest runs. Not for production use. */
export function resetPageviewGateForTesting(): void {
    held = false;
    listeners.clear();
}
